import React, { useEffect, useMemo, useState } from "react";
import useTemplates from "../../../services/Whatsapp/useTemplates";
import "../../whatsapp/WhatsApp.css";
import { toast } from "react-toastify";
import useMedia from "../../../services/Whatsapp/useMedia";

const CampaignTemplate = ({ formData, setFormData, next, back }) => {

  const { templates, fetchTemplatesByUser } = useTemplates();
const {
  uploadImage,
  uploadVideo,
  loading: mediaHookLoading,
} = useMedia();

  const [headerVariables, setHeaderVariables] = useState({});
  const [bodyVariables, setBodyVariables] = useState({});

  const [imageFile, setImageFile] = useState([]);
  const [imagePreview, setImagePreview] = useState("");
  const [imageLink, setImageLink] = useState("");

  const [videoFile, setVideoFile] = useState([]);
  const [videoPreview, setVideoPreview] = useState("");
  const [uploadedMediaUrl, setUploadedMediaUrl] = useState("");

  const [mediaUploadLoading, setMediaUploadLoading] = useState(false);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    fetchTemplatesByUser();
  }, [fetchTemplatesByUser]);

  const approvedTemplates =
    templates?.filter(
      (t) => String(t.status || "").toUpperCase() === "APPROVED"
    ) || [];

  const decodeText = (text = "") => {
    if (!text) return "";

    try {
      return atob(text);
    } catch (err) {
      return text;
    }
  };

  const getComponent = (type) => {
    return formData.template?.components?.find((c) => c.type === type);
  };

  const headerComponent = getComponent("HEADER");
  const bodyComponent = getComponent("BODY");
  const footerComponent = getComponent("FOOTER");
  const buttonComponent = getComponent("BUTTONS");

  const headerText = decodeText(headerComponent?.text || "");
  const bodyText = decodeText(bodyComponent?.text || "");
  const footerText = decodeText(footerComponent?.text || "");
  const buttons = buttonComponent?.buttons || [];

  const isImageTemplate = headerComponent?.format === "IMAGE";
  const isVideoTemplate = headerComponent?.format === "VIDEO";

  const headerParams = useMemo(() => Object.values(headerVariables), [headerVariables]);
  const bodyParams = useMemo(() => Object.values(bodyVariables), [bodyVariables]);

  const clearMediaData = () => {
    setImageFile([]);
    setImagePreview("");
    setImageLink("");

    setVideoFile([]);
    setVideoPreview("");
    setUploadedMediaUrl("");

    setMediaUploadLoading(false);
  };

  const extractVariables = (text = "") => {
    return text.match(/{{\d+}}/g) || [];
  };

  const prepareInitialVariables = (template) => {
    const initialHeaderVariables = {};
    const initialBodyVariables = {};

    template?.components?.forEach((component) => {
      if (!component.text) return;

      const decodedText = decodeText(component.text);
      const matches = extractVariables(decodedText);

      matches.forEach((match) => {
        if (component.type === "HEADER") {
          initialHeaderVariables[match] = "";
        }

        if (component.type === "BODY") {
          initialBodyVariables[match] = "";
        }
      });
    });

    setHeaderVariables(initialHeaderVariables);
    setBodyVariables(initialBodyVariables);

    return {
      initialHeaderVariables,
      initialBodyVariables,
    };
  };

  const handleTemplateSelect = (templateId) => {
    clearMediaData();

    const selected = approvedTemplates.find(
      (t) => String(t.id) === String(templateId)
    );

    if (!selected) {
      setFormData((prev) => ({
        ...prev,
        template: {},
        templateName: "",
        templateID: "",
        templateLanguage: "",
        templateStatus: "",
        templateCategory: "",
        headerParams: [],
        bodyParams: [],
        headerMediaUrl: "",
      }));

      setHeaderVariables({});
      setBodyVariables({});
      return;
    }

    prepareInitialVariables(selected);

    setFormData((prev) => ({
      ...prev,
      template: selected,
      templateName: selected.name || "",
      templateID: selected.id || "",
      templateLanguage: selected.language || "",
      templateStatus: selected.status || "",
      templateCategory: selected.category || "",
      headerParams: [],
      bodyParams: [],
      headerMediaUrl: "",
    }));
  };

  const handleHeaderInputChange = (variable, value) => {
    setHeaderVariables((prev) => ({
      ...prev,
      [variable]: value,
    }));
  };

  const handleBodyInputChange = (variable, value) => {
    setBodyVariables((prev) => ({
      ...prev,
      [variable]: value,
    }));
  };

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      headerParams,
      bodyParams,
      headerMediaUrl: uploadedMediaUrl || imageLink || "",
    }));
  }, [headerParams, bodyParams, uploadedMediaUrl, imageLink, setFormData]);

  const replaceVariables = (text = "", params = []) => {
    return text.replace(/\{\{(\d+)\}\}/g, (match, number) => {
      const index = Number(number) - 1;
      return params[index] || "";
    });
  };

const handleImageUpload = async (event) => {
  const file = event.target.files?.[0];

  if (!file) return;

  setImageFile([file]);
  setImagePreview(URL.createObjectURL(file));

  try {
    setMediaUploadLoading(true);

    const fileNameWithoutExt =
      file.name.substring(0, file.name.lastIndexOf(".")) || file.name;

    const reader = new FileReader();

    reader.onloadend = async () => {
      const fullBase64 = reader.result; 

      const res = await uploadImage(fileNameWithoutExt, fullBase64);

      console.log("Image Upload Response:", res);

      if (res?.status === "0" || res?.responseCode === "00") {
        const mediaUrl =
          res?.image_url ||
          res?.data?.image_url ||
          res?.data?.url ||
          "";

        if (!mediaUrl) {
          toast.error("Image uploaded but image URL not found");
          setMediaUploadLoading(false);
          return;
        }

        setImageLink(mediaUrl);

        setFormData((prev) => ({
          ...prev,
          headerMediaUrl: mediaUrl,
          headerParams: [mediaUrl],
        }));

        toast.success("Image uploaded successfully");
      } else {
        toast.error(res?.responseMessage || "Image upload failed");
        setImageFile([]);
        setImagePreview("");
        setImageLink("");
      }

      setMediaUploadLoading(false);
    };

    reader.onerror = () => {
      toast.error("Failed to read image file");
      setMediaUploadLoading(false);
    };

    reader.readAsDataURL(file);
  } catch (error) {
    console.error("Image upload error:", error);
    toast.error("Image upload failed");
    setMediaUploadLoading(false);
  }
};

const handleVideoUpload = async (event) => {
  const file = event.target.files?.[0];

  if (!file) return;

  setVideoFile([file]);
  setVideoPreview(URL.createObjectURL(file));

  try {
    setMediaUploadLoading(true);

    const res = await uploadVideo(file);

    console.log("Video Upload Response:", res);

    if (res?.responseCode === "00") {
      const mediaUrl =
        res?.data?.mediaId ||
        res?.data?.mediaUrl ||
        res?.data?.url ||
        res?.mediaId ||
        "";

      if (!mediaUrl) {
        toast.error("Video uploaded but media URL not found");
        return;
      }

      setUploadedMediaUrl(mediaUrl);

      setFormData((prev) => ({
        ...prev,
        headerMediaUrl: mediaUrl,
        headerParams: [mediaUrl],
      }));

      toast.success(res?.responseMessage || "Video uploaded successfully");
    } else {
      toast.error(res?.responseMessage || "Video upload failed");
      setVideoFile([]);
      setVideoPreview("");
      setUploadedMediaUrl("");
    }
  } catch (error) {
    console.error("Video upload error:", error);
    toast.error("Video upload failed");
  } finally {
    setMediaUploadLoading(false);
  }
};

  const hasVariablePlaceholder = (template) => {
    if (!template?.components || !Array.isArray(template.components)) return false;

    return template.components.some((component) => {
      const decodedText = decodeText(component.text || "");

      return (
        (component.type === "BODY" || component.type === "HEADER") &&
        /{{\d+}}/.test(decodedText)
      );
    });
  };

  const validateTemplateStep = () => {
    if (!formData.template?.id) {
      toast.error("Select a template");
      return false;
    }

    const selectedTemplate = formData.template;
    const isParam = hasVariablePlaceholder(selectedTemplate);

    if (isParam) {
      const headerVars = extractVariables(headerText);
      const bodyVars = extractVariables(bodyText);

      const missingHeaderParam = headerVars.some(
        (variable) => !String(headerVariables[variable] || "").trim()
      );

      const missingBodyParam = bodyVars.some(
        (variable) => !String(bodyVariables[variable] || "").trim()
      );

      if (missingHeaderParam) {
        toast.error("Please provide Header Params");
        return false;
      }

      if (missingBodyParam) {
        toast.error("Please provide Body Params");
        return false;
      }
    }

  if (isImageTemplate && !imageLink) {
  toast.error("Please upload image in header");
  return false;
}

if (isVideoTemplate && !uploadedMediaUrl) {
  toast.error("Please upload video in header");
  return false;
}

    return true;
  };

const handleNext = () => {
  if (!validateTemplateStep()) return;

  const mediaUrl = uploadedMediaUrl || imageLink || "";

  setFormData((prev) => ({
    ...prev,
    headerParams: mediaUrl ? [mediaUrl] : headerParams,
    bodyParams,
    headerMediaUrl: mediaUrl,
  }));

  next();
};

  useEffect(() => {
    setAnimate(false);

    const timer = setTimeout(() => {
      setAnimate(true);
    }, 10);

    return () => clearTimeout(timer);
  }, [headerText, bodyText, footerText, buttons.length, imagePreview, videoPreview]);

  const finalHeaderText = replaceVariables(headerText, headerParams);
  const finalBodyText = replaceVariables(bodyText, bodyParams);

  return (
    <>
      <div className="w-full px-10 py-6 montserrat-font-1">
        <div className="grid grid-cols-12 gap-10 montserrat-font-1">
          {/* LEFT SECTION */}
          <div className="col-span-7 space-y-8 montserrat-font-1">
            <div>
              <h2 className="text-2xl font-semibold">Select template</h2>
              <p className="text-sm text-gray-500 montserrat-font-2">
                Select the pre-approved template.
              </p>
            </div>

            <select
              value={formData.template?.id || ""}
              onChange={(e) => handleTemplateSelect(e.target.value)}
              className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none"
            >
              <option value="">Select Template</option>

              {approvedTemplates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>

            {formData.template?.id && (
              <div className="grid grid-cols-2 gap-4">
                <div className="border rounded-lg p-4 bg-gray-50">
                  <p className="text-xs text-gray-500">Language</p>
                  <p className="font-medium">{formData.template?.language || "-"}</p>
                </div>

                <div className="border rounded-lg p-4 bg-gray-50">
                  <p className="text-xs text-gray-500">Category</p>
                  <p className="font-medium">{formData.template?.category || "-"}</p>
                </div>

                <div className="border rounded-lg p-4 bg-gray-50">
                  <p className="text-xs text-gray-500">Status</p>
                  <p className="font-medium">{formData.template?.status || "-"}</p>
                </div>

                <div className="border rounded-lg p-4 bg-gray-50">
                  <p className="text-xs text-gray-500">Template ID</p>
                  <p className="font-medium">{formData.template?.id || "-"}</p>
                </div>
              </div>
            )}

            {headerComponent && (
              <div className="border rounded-xl p-6 bg-gray-50 montserrat-font-1">
                <h3 className="text-xl font-semibold mb-3">HEADER</h3>

                {isImageTemplate ? (
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Upload image in header <span className="text-red-500">*</span>
                    </label>

                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="block w-full border rounded-md px-3 py-2 bg-white"
                    />

                    {imagePreview && (
                      <div className="mt-4 flex justify-center">
                        <img
                          src={imagePreview}
                          alt="Uploaded"
                          className="max-w-full max-h-64 rounded-lg border"
                        />
                      </div>
                    )}
                  </div>
                ) : isVideoTemplate ? (
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Upload video in header <span className="text-red-500">*</span>
                    </label>

                    <input
                      type="file"
                      accept="video/*"
                      onChange={handleVideoUpload}
                      className="block w-full border rounded-md px-3 py-2 bg-white"
                    />

                    {mediaUploadLoading ? (
                      <div className="mt-4 text-sm text-gray-500">Uploading video...</div>
                    ) : videoPreview ? (
                      <div className="mt-4 flex justify-center">
                        <video
                          src={videoPreview}
                          controls
                          className="max-w-full max-h-64 rounded-lg border"
                        />
                      </div>
                    ) : null}
                  </div>
                ) : headerText ? (
                  <p className="whitespace-pre-wrap">
                    <span className="font-medium">Text:</span> {headerText}
                  </p>
                ) : null}

                {extractVariables(headerText).map((variable) => (
                  <div key={variable} className="flex items-center gap-3 mt-4">
                    <label className="min-w-[60px] text-sm font-medium">
                      {variable} <span className="text-red-500">*</span>
                    </label>

                    <input
                      type="text"
                      value={headerVariables[variable] || ""}
                      onChange={(e) =>
                        handleHeaderInputChange(variable, e.target.value)
                      }
                      placeholder={`Enter value for ${variable}`}
                      className="flex-1 border rounded-md px-3 py-2 bg-white"
                    />
                  </div>
                ))}
              </div>
            )}

            {bodyText && (
              <div className="border rounded-xl p-6 bg-gray-50 montserrat-font-1">
                <h3 className="text-xl font-semibold mb-3">BODY</h3>

                <p className="whitespace-pre-wrap">
                  <span className="font-medium">Text:</span> {bodyText}
                </p>

                {extractVariables(bodyText).map((variable) => (
                  <div key={variable} className="flex items-center gap-3 mt-4">
                    <label className="min-w-[60px] text-sm font-medium">
                      {variable} <span className="text-red-500">*</span>
                    </label>

                    <input
                      type="text"
                      value={bodyVariables[variable] || ""}
                      onChange={(e) =>
                        handleBodyInputChange(variable, e.target.value)
                      }
                      placeholder={`Enter value for ${variable}`}
                      className="flex-1 border rounded-md px-3 py-2 bg-white"
                    />
                  </div>
                ))}
              </div>
            )}

            {footerText && (
              <div className="border rounded-xl p-6 bg-gray-50 montserrat-font-1">
                <h3 className="text-xl font-semibold mb-3">FOOTER</h3>
                <p className="whitespace-pre-wrap">
                  <span className="font-medium">Text:</span> {footerText}
                </p>
              </div>
            )}

            {buttons.length > 0 && (
              <div className="border rounded-xl p-6 bg-gray-50">
                <h3 className="text-xl font-semibold mb-3">BUTTONS</h3>

                {buttons.map((btn, index) => (
                  <div
                    key={index}
                    className="border px-4 py-2 rounded-md mb-2 bg-white text-sm"
                  >
                    Button {index + 1}. {btn.text}
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-4 pt-6 montserrat-font-1">
              <button onClick={back} className="px-6 py-2 border rounded-md">
                Previous
              </button>

              <button
  onClick={handleNext}
  disabled={mediaUploadLoading || mediaHookLoading}
  className={`px-8 py-2 rounded-md text-white ${
    mediaUploadLoading || mediaHookLoading
      ? "bg-blue-300 cursor-not-allowed"
      : "bg-blue-600 hover:bg-blue-700"
  }`}
>
  {mediaUploadLoading || mediaHookLoading ? "Uploading..." : "Next"}
</button>
            </div>
          </div>

          {/* RIGHT PHONE PREVIEW */}
          <div className="col-span-5 flex justify-center montserrat-font-1">
            <div className="mobile-card">
              <div className="mobile-btn1"></div>
              <div className="mobile-btn2"></div>
              <div className="mobile-btn3"></div>
              <div className="mobile-btn4"></div>

              <div className="mobile-card-int">
                <div className="bubble-container">
                  <div
                    className={`message-bubble ${
                      animate ? "message-bubble-animate" : ""
                    }`}
                  >
                    {videoPreview ? (
                      <div
                        style={{
                          border: "2px solid #ccc",
                          borderRadius: "8px",
                          maxWidth: "90%",
                          height: "200px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          textAlign: "center",
                        }}
                      >
                        <span style={{ fontSize: "18px", color: "#888" }}>
                          Video
                        </span>
                      </div>
                    ) : imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Uploaded"
                        style={{ maxWidth: "90%" }}
                      />
                    ) : finalHeaderText ? (
                      <div className="header-text">{finalHeaderText}</div>
                    ) : null}

                    {finalBodyText && (
                      <div className="body-text py-1">
                        <div>{finalBodyText}</div>
                      </div>
                    )}

                    {footerText && <div className="footer-text">{footerText}</div>}

                    <div className="footer-text text-end">05:27 PM</div>
                  </div>

                  {buttons.length > 0 && (
                    <div
                      className={`button-bubble ${
                        animate ? "button-bubble-animate" : ""
                      }`}
                    >
                      {buttons.map((btn, index) => (
                        <div
                          className="button-text text-primary text-center p-2 mb-1"
                          key={index}
                        >
                          {btn.text}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="mobile-top">
                <div className="mobile-camera">
                  <div className="mobile-int"></div>
                </div>
                <div className="mobile-speaker"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CampaignTemplate;